/**
 * Blog utility functions.
 * Uses direct Firebase Database access like the rest of the codebase.
 */
import {
  getDatabase,
  ref,
  get,
  set,
  push,
  remove,
  update,
} from "firebase/database";
import { getAuth } from "firebase/auth";

const database = getDatabase();
const serverUrl = process.env.REACT_APP_BASE_URL;

// ===================== Utility Functions =====================

function generateSlug(title) {
  if (!title || typeof title !== "string") return "";
  let slug = title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  const suffix = Math.random().toString(36).substring(2, 8);
  return `${slug}-${suffix}`;
}

function calculateReadingTime(content) {
  if (!content || typeof content !== "string") return "1 min read";
  const plainText = content.replace(/<[^>]*>/g, "").trim();
  const words = plainText.split(/\s+/).filter((word) => word.length > 0);
  const minutes = Math.max(1, Math.ceil(words.length / 200));
  return `${minutes} min read`;
}

// ===================== Public Functions =====================

export async function fetchPublicBlogs({ type, limit = 10, page = 1 } = {}) {
  try {
    const blogsRef = ref(database, "blogs");
    const snapshot = await get(blogsRef);
    const blogs = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const blog = child.val();
        if (blog.status === "published") {
          const { content, ...blogWithoutContent } = blog;
          blogs.push({ id: child.key, ...blogWithoutContent });
        }
      });
    }

    // Sort by publishedAt descending
    blogs.sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0));

    // Filter by type if provided
    const filteredBlogs = type
      ? blogs.filter((b) => b.type === type)
      : blogs;

    // Pagination
    const total = filteredBlogs.length;
    const totalPages = Math.ceil(total / limit);
    const startIndex = (page - 1) * limit;
    const paginatedBlogs = filteredBlogs.slice(startIndex, startIndex + limit);

    return {
      success: true,
      blogs: paginatedBlogs,
      pagination: { page, limit, total, totalPages },
    };
  } catch (error) {
    console.error("Error fetching blogs:", error);
    throw error;
  }
}

export async function fetchPublicBlogBySlug(slug) {
  try {
    // Get blog ID from slug mapping
    const slugRef = ref(database, `blogSlugToId/${slug}`);
    const slugSnapshot = await get(slugRef);

    if (!slugSnapshot.exists()) {
      throw new Error("Blog not found");
    }

    const blogId = slugSnapshot.val();
    const blogRef = ref(database, `blogs/${blogId}`);
    const blogSnapshot = await get(blogRef);

    if (!blogSnapshot.exists()) {
      throw new Error("Blog not found");
    }

    const blog = blogSnapshot.val();

    if (blog.status !== "published") {
      throw new Error("Blog not found");
    }

    // Increment views
    const newViews = (blog.views || 0) + 1;
    await update(ref(database, `blogs/${blogId}`), { views: newViews });

    // Get related posts
    const allBlogsRef = ref(database, "blogs");
    const allSnapshot = await get(allBlogsRef);
    const relatedPosts = [];

    if (allSnapshot.exists()) {
      allSnapshot.forEach((child) => {
        if (child.key === blogId) return;
        const otherBlog = child.val();
        if (otherBlog.status !== "published") return;

        const sameType = otherBlog.type && otherBlog.type === blog.type;
        const sharedTags =
          blog.tags &&
          otherBlog.tags &&
          blog.tags.some((tag) => otherBlog.tags.includes(tag));

        if (sameType || sharedTags) {
          const { content, ...blogWithoutContent } = otherBlog;
          relatedPosts.push({ id: child.key, ...blogWithoutContent });
        }
      });
    }

    relatedPosts.sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0));

    return {
      success: true,
      blog: { id: blogId, ...blog, views: newViews },
      relatedPosts: relatedPosts.slice(0, 4),
    };
  } catch (error) {
    console.error("Error fetching blog:", error);
    throw error;
  }
}

// ===================== Admin Functions =====================

export async function fetchAdminBlogs() {
  try {
    const blogsRef = ref(database, "blogs");
    const snapshot = await get(blogsRef);
    const blogs = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        blogs.push({ id: child.key, ...child.val() });
      });
    }

    // Sort by createdAt descending
    blogs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    return { success: true, blogs };
  } catch (error) {
    console.error("Error fetching admin blogs:", error);
    throw error;
  }
}

export async function createBlog(blogData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("Not authenticated");
    }

    const slug = generateSlug(blogData.title);
    const readingTime = calculateReadingTime(blogData.content);
    const now = Date.now();

    const newBlogData = {
      title: blogData.title?.trim() || "",
      slug,
      content: blogData.content || "",
      excerpt: blogData.excerpt?.trim() || "",
      coverImage: blogData.coverImage || "",
      tags: blogData.tags || [],
      type: blogData.type || "general",
      status: blogData.status || "draft",
      seo: {
        metaTitle: blogData.seo?.metaTitle || "",
        metaDescription: blogData.seo?.metaDescription || "",
      },
      views: 0,
      readingTime,
      author: {
        id: user.uid,
        name: user.displayName || "Admin",
      },
      createdAt: now,
      updatedAt: now,
      publishedAt: blogData.status === "published" ? now : null,
    };

    // Push new blog
    const blogsRef = ref(database, "blogs");
    const newBlogRef = push(blogsRef);
    await set(newBlogRef, newBlogData);

    // Create slug-to-id mapping
    const slugRef = ref(database, `blogSlugToId/${slug}`);
    await set(slugRef, newBlogRef.key);

    return {
      success: true,
      id: newBlogRef.key,
      blog: { id: newBlogRef.key, ...newBlogData },
    };
  } catch (error) {
    console.error("Error creating blog:", error);
    throw error;
  }
}

export async function updateBlog(id, blogData) {
  try {
    const blogRef = ref(database, `blogs/${id}`);
    const snapshot = await get(blogRef);

    if (!snapshot.exists()) {
      throw new Error("Blog not found");
    }

    const existingBlog = snapshot.val();
    const now = Date.now();

    // Handle slug update if title changed
    let slug = existingBlog.slug;
    if (blogData.title && blogData.title.trim() !== existingBlog.title) {
      // Remove old slug mapping
      if (existingBlog.slug) {
        await remove(ref(database, `blogSlugToId/${existingBlog.slug}`));
      }
      slug = generateSlug(blogData.title);
      await set(ref(database, `blogSlugToId/${slug}`), id);
    }

    const readingTime = blogData.content
      ? calculateReadingTime(blogData.content)
      : existingBlog.readingTime;

    let publishedAt = existingBlog.publishedAt;
    if (blogData.status === "published" && !existingBlog.publishedAt) {
      publishedAt = now;
    }

    const updateData = {
      title: blogData.title?.trim() || existingBlog.title,
      slug,
      content: blogData.content || existingBlog.content,
      excerpt: blogData.excerpt !== undefined ? blogData.excerpt.trim() : existingBlog.excerpt,
      coverImage: blogData.coverImage !== undefined ? blogData.coverImage : existingBlog.coverImage,
      tags: blogData.tags !== undefined ? blogData.tags : existingBlog.tags,
      type: blogData.type !== undefined ? blogData.type : existingBlog.type,
      status: blogData.status || existingBlog.status,
      seo: {
        metaTitle: blogData.seo?.metaTitle !== undefined ? blogData.seo.metaTitle : (existingBlog.seo?.metaTitle || ""),
        metaDescription: blogData.seo?.metaDescription !== undefined ? blogData.seo.metaDescription : (existingBlog.seo?.metaDescription || ""),
      },
      readingTime,
      updatedAt: now,
      publishedAt,
      views: existingBlog.views || 0,
      author: existingBlog.author,
      createdAt: existingBlog.createdAt,
    };

    await set(blogRef, updateData);

    return { success: true, blog: { id, ...updateData } };
  } catch (error) {
    console.error("Error updating blog:", error);
    throw error;
  }
}

export async function deleteBlog(id) {
  try {
    const blogRef = ref(database, `blogs/${id}`);
    const snapshot = await get(blogRef);

    if (!snapshot.exists()) {
      throw new Error("Blog not found");
    }

    const blog = snapshot.val();

    // Remove slug mapping
    if (blog.slug) {
      await remove(ref(database, `blogSlugToId/${blog.slug}`));
    }

    // Remove blog
    await remove(blogRef);

    return { success: true, message: "Blog deleted successfully" };
  } catch (error) {
    console.error("Error deleting blog:", error);
    throw error;
  }
}

// ===================== Image Upload =====================

export async function uploadBlogImage(file) {
  const formData = new FormData();
  // When file is a Blob (e.g. from canvas cropping), provide a filename
  // so the server-side multer generates a proper filename.
  if (file instanceof Blob && !(file instanceof File)) {
    formData.append("file", file, "blog-cover.jpg");
  } else {
    formData.append("file", file);
  }

  const res = await fetch(`${serverUrl}/upload-featured-image`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) throw new Error("Image upload failed");
  const data = await res.json();
  return `${serverUrl}/images/${data.file_name}`;
}
